import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, Quote, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { HERO, HIGHLIGHTS, SERVICES, APPROACH, TESTIMONIALS, COMPANY } from "../data/site";

const Section = ({ children, className = "" }) => (
  <section className={`py-20 md:py-28 px-5 md:px-10 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-lightBlue">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center py-16 md:py-24">
          <div className="fade-up">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-4" data-testid="hero-eyebrow">
              {HERO.eyebrow}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-brand-darkBlue leading-[1.08]">
              {HERO.heading}
            </h1>
            <p className="mt-4 text-2xl font-medium text-brand-red font-heading">{COMPANY.tagline}</p>
            <p className="mt-6 text-lg text-gray-800 leading-relaxed max-w-xl">{HERO.body}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact#consultation" data-testid="hero-book-btn" className="inline-flex items-center gap-2 bg-brand-red text-white font-semibold py-4 px-8 rounded-full hover:bg-brand-redDark transition-colors focus:outline-none focus:ring-4 focus:ring-red-200">
                Book Consultation <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact#apply" data-testid="hero-enroll-btn" className="inline-flex items-center bg-brand-darkBlue text-white font-semibold py-4 px-8 rounded-full hover:bg-brand-blue transition-colors focus:outline-none focus:ring-4 focus:ring-blue-200">
                Enroll
              </Link>
              <Link to="/contact" data-testid="hero-contact-btn" className="inline-flex items-center border-2 border-brand-darkBlue text-brand-darkBlue font-semibold py-4 px-8 rounded-full hover:bg-white transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <img src={HERO.image} alt="Nurse caring for an elderly patient" className="rounded-3xl shadow-2xl w-full h-[440px] object-cover" data-testid="hero-image" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden sm:flex items-center gap-3 border border-border">
                <div className="flex text-brand-red">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                </div>
                <span className="text-brand-darkBlue font-semibold">Trusted by families</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights strip */}
      <section className="bg-brand-darkBlue">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid sm:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((h) => (
            <div key={h} className="flex items-center gap-3 text-white" data-testid="highlight-item">
              <CheckCircle2 className="w-7 h-7 text-brand-red shrink-0" />
              <span className="text-lg font-medium">{h}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <Section>
        <Reveal className="max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Services We Offer</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-darkBlue">Expert In-Home Health Staffing</h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {SERVICES.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Heart;
            return (
              <Reveal key={s.slug} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="h-full bg-brand-lightBlue border border-blue-200 rounded-2xl p-8 hover:shadow-xl transition-shadow"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-brand-darkBlue flex items-center justify-center mb-5">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-brand-darkBlue mb-3">{s.title}</h3>
                  <p className="text-lg text-gray-800 leading-relaxed">{s.description}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-10">
          <Link to="/services" data-testid="home-view-services" className="inline-flex items-center gap-2 text-brand-red font-semibold text-lg hover:gap-3 transition-all">
            View all services <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </Section>

      {/* Multidisciplinary Approach */}
      <section className="bg-brand-lightBlue py-20 md:py-28 px-5 md:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <motion.img
                src={APPROACH.image}
                alt="Caregiver with an elderly couple"
                className="w-full h-[420px] object-cover"
                data-testid="approach-image"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-darkBlue">{APPROACH.heading}</h2>
            <p className="mt-4 text-lg text-gray-800">{APPROACH.intro}</p>
            <p className="mt-6 text-2xl font-semibold text-brand-red font-heading">{APPROACH.motto}</p>
            <div className="mt-5 space-y-4">
              {APPROACH.paragraphs.map((p, i) => (
                <p key={i} className="text-lg text-gray-800 leading-relaxed">{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <Section>
        <Reveal className="max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Testimonials</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-darkBlue">Hear From Our Happy Clients</h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.author} delay={i * 0.1}>
              <figure className="h-full bg-white border border-border rounded-2xl p-8 shadow-md flex flex-col" data-testid="testimonial-card">
                <Quote className="w-10 h-10 text-brand-red/30 mb-4" />
                <blockquote className="text-lg text-gray-800 leading-relaxed flex-1">{t.quote}</blockquote>
                <figcaption className="mt-6 font-semibold text-brand-darkBlue">— {t.author}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-brand-darkBlue py-20 px-5 md:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to give your loved one the care they deserve?</h2>
          <p className="mt-4 text-lg text-white/80">Book a consultation with our team today and experience the Above and Beyond difference.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact#consultation" data-testid="cta-book-btn" className="bg-brand-red text-white font-semibold py-4 px-8 rounded-full hover:bg-brand-redDark transition-colors">Book Consultation</Link>
            <Link to="/contact#apply" data-testid="cta-apply-btn" className="border-2 border-white text-white font-semibold py-4 px-8 rounded-full hover:bg-white hover:text-brand-darkBlue transition-colors">Join Our Team</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
